/**
 * Permission Service
 *
 * Centralized permission checking service for the PWA.
 * This service allows for declarative permission definitions that can
 * be loaded from a cloud service and cached locally.
 *
 * Usage:
 *   import { permissionService } from '@/services/PermissionService'
 *
 *   if (permissionService.can('viewEmail', { brand, rep })) {
 *     // Show email
 *   }
 *
 *   const filteredData = permissionService.filter(
 *     data,
 *     'viewAccommodation',
 *     { brand, rep }
 *   )
 */

import { isOfficeStaff } from '@/constants/staff.js'

const STORAGE_KEY = 'snowhub_permissions'

/**
 * Default permission definitions (used when server is unavailable)
 * Each permission has:
 * - name: string identifier
 * - check: function(context) => boolean - determines if permission is granted
 * - filter: (optional) function(item, context) => boolean - filters data arrays
 * - description: (optional) human-readable description
 */
const DEFAULT_PERMISSIONS = {
    // Transfer run status (TL-06). The API decides and returns the statuses the
    // rep may set on each run (`canSetStatus`); this mirrors it so controls are
    // hidden rather than failing with a 403.
    setTransferStatus: {
        name: 'setTransferStatus',
        description: 'Can set this status on this transfer run',
        check: ({status, canSetStatus}) => Array.isArray(canSetStatus) && canSetStatus.includes(status)
    },

    // KD-05: marking keys ready from the PWA. Mirrors the API rule in
    // api/src/modules/KeyDistribution/repStatus.js ("Week admins or level 3");
    // the API is the authority and reports isWeekAdmin through
    // GET /api/keyDist/canMarkReady.
    markKeysReady: {
        name: 'markKeysReady',
        description: 'Can mark rooms ready (level 3, or a week admin for the week)',
        check: ({rep, isWeekAdmin}) => {
            if (!rep) return false
            return rep.level >= 3 || !!isWeekAdmin
        }
    },

    // Email visibility permissions
    viewEmail: {
        name: 'viewEmail',
        description: 'Can view passenger email addresses',
        check: ({rep}) => {
            return rep?.level >= 1
        }
    },

    // Sensitive data permissions (NOK, DOB)
    viewSensitiveData: {
        name: 'viewSensitiveData',
        description: 'Can view next of kin and date of birth',
        check: ({rep, brand}) => {
            if (!rep) return false

            // Wasteland brand requires admin level
            if (brand === 'wasteland') {
                return rep.level >= 3
            }

            // Other brands: manager level or above
            return rep.level >= 2
        }
    },

    // Lift pass permissions
    viewAllLiftPasses: {
        name: 'viewAllLiftPasses',
        description: 'Can view all lift passes for all locations (level 3 non-snowfuze)',
        check: ({rep, brand}) => {
            return rep?.level === 3 && brand !== 'snowfuze'
        }
    },

    canDistributeLiftPass: {
        name: 'canDistributeLiftPass',
        description: 'Can distribute a lift pass based on brand matching',
        check: ({brand, ref}) => {
            if (!ref || !brand) return false
            const passBrand = ref.split("-")[0].toUpperCase()
            // Can distribute if brand matches or pass is WHITEROAD
            return passBrand === brand.toUpperCase() || passBrand === 'WHITEROAD'
        }
    },

    // Accommodation data permissions
    viewAllAccommodation: {
        name: 'viewAllAccommodation',
        description: 'Can view accommodation for all brands',
        check: ({rep}) => {
            return rep?.level >= 3
        },
        filter: (item, {rep, brand}) => {
            // Level 3+ sees every brand. This used to be implicit in filter(),
            // which skipped the filter entirely when the check passed; the
            // filter now always runs, so the rule has to state it.
            if (rep?.level >= 3) return true

            const itemBrand = item.reference?.split("-")[0].toUpperCase()
            return itemBrand === brand.toUpperCase()
        }
    },

    // Staff viewing permissions
    viewAllStaff: {
        name: 'viewAllStaff',
        description: 'Can view all staff regardless of brand',
        check: ({rep, brand}) => {
            // Level 3 (admin) can view all staff, or snowfuze level 2+ can view all
            return rep?.level >= 3 || (brand === 'snowfuze' && rep?.level >= 2)
        }
    },

    viewCrossBrandStaff: {
        name: 'viewCrossBrandStaff',
        description: 'Can view office (Office - Delivery) staff of any brand',
        check: ({rep}) => {
            return rep?.level >= 3
        }
    },

    viewTransportPassenger: {
        name: 'viewTransportPassenger',
        description: 'Can view transport passenger details',
        check: ({rep, brand, ref}) => {
            // Level 2+ can view any passenger
            if (rep?.level >= 2) {
                return true
            }

            // Otherwise, only allow if passenger's brand matches current brand
            if (!ref || !brand) return false
            const refBrand = ref.split("-")[0].toUpperCase()
            return refBrand === brand.toUpperCase()
        }
    },

    viewStaff: {
        name: 'viewStaff',
        description: 'Can view staff based on level and brand',
        check: ({rep}) => {
            return rep?.level >= 1 // All reps can view some staff
        },
        filter: (staffMember, {rep, brand}) => {
            if (!rep) return false

            // Level 3 (admin) can see all staff
            if (rep.level >= 3) {
                return true
            }

            // Snowfuze brand: level 2+ can see all
            if (brand === 'snowfuze' && rep.level >= 2) {
                return true
            }

            // Level 2 (manager) for non-snowfuze: office staff of any brand + own brand
            if (rep.level >= 2) {
                return isOfficeStaff(staffMember) || staffMember.tenant === brand
            }

            // Level 1 (standard): own brand only
            return staffMember.tenant === brand
        }
    },

    // Passenger viewing permissions
    viewAllPassengers: {
        name: 'viewAllPassengers',
        description: 'Can view passengers from all brands',
        check: ({rep, brand}) => {
            return brand === 'snowfuze' && rep?.level >= 2
        }
    },

    canViewPassenger: {
        name: 'canViewPassenger',
        description: 'Can view a specific passenger page by reference',
        check: ({rep, brand, ref}) => {
            // Level 2+ (manager/admin) can view any passenger
            if (rep?.level >= 2) {
                return true
            }

            // Otherwise, only allow if passenger's brand matches current brand
            if (!ref || !brand) return false
            const refBrand = ref.split("-")[0].toUpperCase()
            return refBrand === brand.toUpperCase()
        }
    },

    viewPassenger: {
        name: 'viewPassenger',
        description: 'Can view passenger based on brand filtering',
        check: ({rep}) => {
            return rep?.level >= 1 // All reps can view some passengers
        },
        filter: (passenger, {rep, brand}) => {
            if (!passenger?.reference) return false
            const passengerBrand = passenger.reference.split("-")[0].toUpperCase()

            // Level 2+ snowfuze users can see all passengers
            if (brand === 'snowfuze' && rep?.level >= 2) {
                return true
            }

            // Level 3 (admin) can see all passengers
            if (rep?.level >= 3) {
                return true
            }

            // WHITEROAD passengers are always visible
            if (passengerBrand === 'WHITEROAD') {
                return true
            }

            // Otherwise, filter by brand match
            return passengerBrand === brand.toUpperCase()
        }
    },

    // Rep viewing permissions
    viewRep: {
        name: 'viewRep',
        description: 'Can view rep/staff member details',
        check: ({rep}) => {
            return rep?.level >= 1
        },
        filter: (repData, {rep, brand}) => {
            if (!repData || !rep) return false

            // Level 3 (admin) can see all reps
            if (rep.level >= 3) {
                return true
            }

            // Snowfuze brand: level 2+ can see all
            if (brand === 'snowfuze' && rep.level >= 2) {
                return true
            }

            // Level 2 (manager) for non-snowfuze: office staff of any brand + own brand
            if (rep.level >= 2) {
                return isOfficeStaff(repData) || repData.tenant === brand
            }

            // Level 1 (standard): own brand only
            return repData.tenant === brand
        }
    },

    // ========================================================================
    // UTILITY PERMISSIONS - Level-only and Brand-only checks
    // ========================================================================

    // Level-only checks (no brand consideration)
    // Level 1 = Standard user, Level 2 = Manager, Level 3 = Admin
    hasLevel1: {
        name: 'hasLevel1',
        description: 'Rep has level 1+ (standard user or above)',
        check: ({rep}) => rep?.level >= 1
    },

    hasLevel2: {
        name: 'hasLevel2',
        description: 'Rep has level 2+ (manager or above)',
        check: ({rep}) => rep?.level >= 2
    },

    hasLevel3: {
        name: 'hasLevel3',
        description: 'Rep has level 3 (admin only)',
        check: ({rep}) => rep?.level >= 3
    },

    // Levels stop at 3. There is deliberately no hasLevel4/hasLevel5.

    // Brand-only checks (no level consideration)
    isSnowfuze: {
        name: 'isSnowfuze',
        description: 'Current brand is Snowfuze',
        check: ({brand}) => brand === 'snowfuze'
    },

    isNuco: {
        name: 'isNuco',
        description: 'Current brand is Nuco',
        check: ({brand}) => brand === 'nuco'
    },

    isWasteland: {
        name: 'isWasteland',
        description: 'Current brand is Wasteland',
        check: ({brand}) => brand === 'wasteland'
    },

    isWhiteroad: {
        name: 'isWhiteroad',
        description: 'Current brand is Whiteroad',
        check: ({brand}) => brand === 'whiteroad'
    },

    isThursday: {
        name: 'isThursday',
        description: 'Current brand is Thursday',
        check: ({brand}) => brand === 'thursday'
    },

    // Brand filtering utilities
    filterByOwnBrand: {
        name: 'filterByOwnBrand',
        description: 'Filter items by current brand (via reference field)',
        check: ({rep}) => rep?.level >= 1, // All reps can filter
        filter: (item, {brand}) => {
            if (!item.reference) return false
            const itemBrand = item.reference.split("-")[0].toUpperCase()
            return itemBrand === brand.toUpperCase()
        }
    },

    filterByTenant: {
        name: 'filterByTenant',
        description: 'Filter items by current brand (via tenant field)',
        check: ({rep}) => rep?.level >= 1, // All reps can filter
        filter: (item, {brand}) => {
            return item.tenant === brand
        }
    }
}

/**
 * Compile a string expression into a function
 * @param {string} expr - Expression string like "rep?.level >= 1"
 * @param {Array<string>} params - Parameter names available in the expression
 * @returns {Function} - Compiled function
 */
function compileExpression(expr, params = ['rep', 'brand', 'ref', 'item']) {
    if (!expr || typeof expr !== 'string') {
        return () => false
    }
    try {
        // Create a function that takes a context object and extracts the parameters
        return new Function('ctx', `
            const { ${params.join(', ')} } = ctx || {};
            try {
                return ${expr};
            } catch (e) {
                console.warn('Expression evaluation error:', e);
                return false;
            }
        `)
    } catch (e) {
        console.error('Failed to compile expression:', expr, e)
        return () => false
    }
}

/**
 * Convert server permission format to runtime format
 * @param {Object} serverPermissions - Permissions from server with checkExpr/filterExpr
 * @returns {Object} - Permissions with compiled check/filter functions
 */
function compileServerPermissions(serverPermissions) {
    const compiled = {}

    for (const [key, perm] of Object.entries(serverPermissions)) {
        compiled[key] = {
            name: perm.name || key,
            description: perm.description || ''
        }

        // Compile check expression
        if (perm.checkExpr) {
            compiled[key].check = (ctx) => compileExpression(perm.checkExpr)(ctx)
        } else if (perm.check && typeof perm.check === 'string') {
            // Legacy format where check is directly a string
            compiled[key].check = (ctx) => compileExpression(perm.check)(ctx)
        } else {
            compiled[key].check = () => false
        }

        // Compile filter expression
        if (perm.filterExpr) {
            const filterFn = compileExpression(perm.filterExpr, ['item', 'rep', 'brand', 'ref'])
            compiled[key].filter = (item, ctx) => filterFn({ ...ctx, item })
        } else if (perm.filter && typeof perm.filter === 'string') {
            const filterFn = compileExpression(perm.filter, ['item', 'rep', 'brand', 'ref'])
            compiled[key].filter = (item, ctx) => filterFn({ ...ctx, item })
        }
    }

    return compiled
}

/**
 * Permission Service Class
 */
class PermissionService {
    constructor(permissions = DEFAULT_PERMISSIONS) {
        this.permissions = permissions
        this.serverVersion = null
        this.isLoaded = false
    }

    /**
     * Check if a permission is granted
     * @param {string} action - Permission action name
     * @param {Object} context - Context object containing { rep, brand, ...other }
     * @param {Object} context.rep - Rep object with level property
     * @param {string} context.brand - Brand name
     * @returns {boolean} - Whether permission is granted
     */
    can(action, context = {}) {
        const permission = this.permissions[action]

        if (!permission) {
            console.warn(`Permission '${action}' not defined`)
            return false
        }

        try {
            return permission.check(context)
        } catch (error) {
            console.error(`Error checking permission '${action}':`, error)
            return false
        }
    }

    /**
     * Filter an array based on permission rules
     * @param {Array} data - Array of items to filter
     * @param {string} action - Permission action name
     * @param {Object} context - Context object containing { rep, brand, ...other }
     * @returns {Array} - Filtered array
     */
    filter(data, action, context = {}) {
        if (!Array.isArray(data)) {
            console.warn('PermissionService.filter expects an array')
            return []
        }

        const permission = this.permissions[action]

        if (!permission) {
            console.warn(`Permission '${action}' not defined`)
            return []
        }

        // Where a rule defines a filter, that filter is the whole rule: it
        // decides per item, including who sees everything.
        //
        // This used to run check() first and return the array untouched when it
        // passed, on the reading that check means "can view ALL". But viewStaff,
        // viewRep, filterByOwnBrand and filterByTenant all use check for "this
        // rep can view SOME" (rep.level >= 1) and put the real per-level,
        // per-brand rules in filter. Every rep therefore passed the check and
        // the filter body never ran: SearchStaff.vue showed a level 1 rep all
        // staff of every brand, and RepPage.vue let them open any rep. The
        // filters already handle the see-everything cases themselves
        // ('if (rep.level >= 3) return true'), so applying them unconditionally
        // is what those rules were always written for.
        if (permission.filter) {
            try {
                return data.filter(item => permission.filter(item, context))
            } catch (error) {
                console.error(`Error filtering with permission '${action}':`, error)
                return []
            }
        }

        // No filter to apply, so the check decides all of it or none of it.
        try {
            return permission.check(context) ? data : []
        } catch (error) {
            console.error(`Error checking permission '${action}':`, error)
            return []
        }
    }

    /**
     * Load permissions from external source (e.g., cloud service)
     * @param {Object} permissions - Permission definitions object
     */
    loadPermissions(permissions) {
        this.permissions = {...this.permissions, ...permissions}
    }

    /**
     * Get all available permissions
     * @returns {Object} - All permission definitions
     */
    getPermissions() {
        return this.permissions
    }

    /**
     * Get a specific permission definition
     * @param {string} action - Permission action name
     * @returns {Object|null} - Permission definition or null
     */
    getPermission(action) {
        return this.permissions[action] || null
    }

    /**
     * Check multiple permissions at once
     * @param {Array<string>} actions - Array of permission action names
     * @param {Object} context - Context object
     * @returns {Object} - Object with action names as keys and boolean results
     */
    canMultiple(actions, context = {}) {
        const results = {}
        actions.forEach(action => {
            results[action] = this.can(action, context)
        })
        return results
    }

    /**
     * Load permissions from cache (localStorage)
     * @returns {boolean} - Whether cache was loaded successfully
     */
    loadFromCache() {
        try {
            const cached = localStorage.getItem(STORAGE_KEY)
            if (!cached) return false

            const data = JSON.parse(cached)
            if (!data.permissions || !data.version) return false

            // Compile and merge server permissions with defaults
            const serverPermissions = compileServerPermissions(data.permissions)
            this.permissions = { ...DEFAULT_PERMISSIONS, ...serverPermissions }
            this.serverVersion = data.version
            this.isLoaded = true
            console.log(`Loaded permissions from cache (v${data.version})`)
            return true
        } catch (e) {
            console.warn('Failed to load permissions from cache:', e)
            return false
        }
    }

    /**
     * Save permissions to cache (localStorage)
     * @param {Object} data - Server response with version and permissions
     */
    saveToCache(data) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                version: data.version,
                updatedAt: data.updatedAt,
                permissions: data.permissions
            }))
        } catch (e) {
            console.warn('Failed to save permissions to cache:', e)
        }
    }

    /**
     * Load permissions from server
     * Should be called after user authentication
     * @param {Function} apiFetch - API fetch function (e.g., api.get)
     * @returns {Promise<boolean>} - Whether load was successful
     */
    async loadFromServer(apiFetch) {
        try {
            const response = await apiFetch('/api/app-permissions')

            // No active version on server - continue with defaults
            if (!response.permissions || response.version === null) {
                console.log('No server permissions configured, using defaults')
                return false
            }

            // Check if we already have this version
            if (this.serverVersion === response.version) {
                console.log(`Already have permission version ${response.version}`)
                return true
            }

            // Compile and apply new permissions
            const serverPermissions = compileServerPermissions(response.permissions)
            this.permissions = { ...DEFAULT_PERMISSIONS, ...serverPermissions }
            this.serverVersion = response.version
            this.isLoaded = true

            // Save to cache
            this.saveToCache(response)

            console.log(`Loaded permissions from server (v${response.version})`)
            return true
        } catch (e) {
            console.warn('Failed to load permissions from server:', e)
            // Try loading from cache as fallback
            if (!this.isLoaded) {
                this.loadFromCache()
            }
            return false
        }
    }

    /**
     * Clear cached permissions
     */
    clearCache() {
        try {
            localStorage.removeItem(STORAGE_KEY)
            this.permissions = DEFAULT_PERMISSIONS
            this.serverVersion = null
            this.isLoaded = false
        } catch (e) {
            console.warn('Failed to clear permissions cache:', e)
        }
    }

    /**
     * Get current server version
     * @returns {number|null} - Current version or null if using defaults
     */
    getVersion() {
        return this.serverVersion
    }
}

// Export singleton instance
export const permissionService = new PermissionService()

// Export class for testing or custom instances
export {PermissionService}

// Export default permission definitions for reference
export {DEFAULT_PERMISSIONS}
